export type ErrorCategoryType = 'network' | 'auth' | 'data' | 'widget' | 'unknown';

export interface ErrorCategory {
  type: ErrorCategoryType;
  userMessage: string;
}

const NETWORK_FIREBASE_CODES = new Set([
  'unavailable',
  'deadline-exceeded',
  'network-request-failed',
  'cancelled',
  'internal',
]);

const AUTH_FIREBASE_CODES = new Set(['permission-denied', 'unauthenticated']);

const NETWORK_MESSAGE_PATTERNS = [
  'failed to fetch',
  'networkerror',
  'unavailableerror',
  'network request failed',
  'load failed',
  'client is offline',
];

function looksLikeFirebaseError(error: unknown): error is { code: string; name?: string } {
  if (!error || typeof error !== 'object') return false;
  const e = error as { code?: unknown; name?: unknown };
  return typeof e.code === 'string';
}

function firebaseStoragePluginError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const code = (error as { code?: string }).code;
  return typeof code === 'string' && code.startsWith('storage/');
}

function stripCallablePrefix(code: string): string {
  return code.startsWith('functions/') ? code.slice('functions/'.length) : code;
}

function errorMessageString(error: unknown): string {
  if (error == null) return '';
  if (error instanceof Error) return error.message || '';
  if (typeof error === 'string') return error;
  try {
    return String(error);
  } catch {
    return '';
  }
}

function isNetworkError(error: unknown): boolean {
  if (looksLikeFirebaseError(error) && NETWORK_FIREBASE_CODES.has(stripCallablePrefix(error.code))) {
    return true;
  }
  const message = errorMessageString(error).toLowerCase();
  return NETWORK_MESSAGE_PATTERNS.some((pattern) => message.includes(pattern));
}

function isAuthError(error: unknown): boolean {
  if (firebaseStoragePluginError(error)) return false;
  if (looksLikeFirebaseError(error)) {
    const code = stripCallablePrefix(error.code);
    if (AUTH_FIREBASE_CODES.has(code)) return true;
    if (code.startsWith('auth/')) return true;
  }
  return false;
}

function isDataError(error: unknown): boolean {
  if (error instanceof TypeError) return true;
  if (error instanceof SyntaxError) return true;
  if (typeof RangeError !== 'undefined' && error instanceof RangeError) return true;
  return false;
}

function isWidgetError(error: unknown): boolean {
  const message = errorMessageString(error).toLowerCase();
  return (
    message.includes('hydration')
    || (message.includes('react') && message.includes('render'))
  );
}

export function categorizeError(error: unknown): ErrorCategory {
  if (isNetworkError(error)) {
    return {
      type: 'network',
      userMessage: 'Connection issue. Please check your internet and try again.',
    };
  }
  if (isAuthError(error)) {
    return {
      type: 'auth',
      userMessage: 'Authentication error. Please sign in again.',
    };
  }
  if (isDataError(error)) {
    return {
      type: 'data',
      userMessage: 'Unable to load data. Please refresh the page.',
    };
  }
  if (isWidgetError(error)) {
    return {
      type: 'widget',
      userMessage: 'Unable to display this content. Please refresh the page.',
    };
  }
  return {
    type: 'unknown',
    userMessage: 'Something went wrong. Please refresh the page.',
  };
}

function isStorageQuotaError(error: unknown): boolean {
  if (error instanceof DOMException && error.name === 'QuotaExceededError') return true;
  const message = errorMessageString(error).toLowerCase();
  return message.includes('quota') && message.includes('exceeded');
}

export function shouldDowngradeToWarning(error: unknown): boolean {
  if (isStorageQuotaError(error)) return true;
  const category = categorizeError(error);
  return category.type === 'network' || category.type === 'auth';
}
